import { convertMediaTypeToRhinoAssetType } from '../../../data/compat/rhinoAssetTypeToMediaType';
import { tvpil } from '../../../data/compat/tvpil';
import {
  TopShelfAsset,
  TopShelfEpisodeAsset,
  TopShelfMovieAsset,
  TopShelfPackageAsset,
  TopShelfProgramAsset,
  TopShelfSeriesAsset,
} from '../../../data/topShelf/types';
import { KalturaTopShelfAsset } from '../../composed';
import { fromKalturaBoolean } from '../../utils';
import { processPhoenixImages } from '../processing';
import { getProviderNameFromMeta, getRating } from '../utils';
import { processVodAssetMetas } from '../vod';

const META_MAX_GENRES = 3;
const META_MAX_AUDIO_LANGUAGES = 4;

const processors = {
  episode: processEpisode,
  movie: processMovie,
  program: processProgram,
  series: processSeries,
  package: processPackage,
};

export function processTopShelfAsset(asset: KalturaTopShelfAsset): TopShelfAsset {
  const type = convertMediaTypeToRhinoAssetType(tvpil.dms.mediaTypesValue[asset.type]);

  const process = processors[type as keyof typeof processors] ?? processMovie;

  return process(asset);
}

function processCommonTopShelfAsset(asset: KalturaTopShelfAsset) {
  return {
    description: asset.description,
    id: asset.id,
    images: processPhoenixImages(asset.images),
    isAdult: fromKalturaBoolean(asset.metas['Adult']),
    rating: getRating(asset),
    title: asset.name,
    ...(asset.tags['AudioLanguage'] && {
      audioLanguages: asset.tags['AudioLanguage'].objects.slice(0, META_MAX_AUDIO_LANGUAGES).map(({ value }) => value),
    }),
    ...(asset.tags['audioType'] && {
      audioTypes: asset.tags['audioType'].objects.map(({ value }) => value),
    }),
    ...(asset.tags['Country'] && {
      country: asset.tags['Country'].objects[0]?.value,
    }),
    ...(asset.tags['CountryOfOrigin'] && {
      countryOfOrigin: asset.tags['CountryOfOrigin'].objects[0]?.value,
    }),
    ...(asset.tags['Genre'] && {
      genres: asset.tags['Genre'].objects.slice(0, META_MAX_GENRES).map(({ value }) => value),
    }),
    ...(asset.tags['ParentalRating'] && {
      parentalRating: Number(asset.tags['ParentalRating'].objects[0].value),
    }),
    ...(asset.tags['Quality'] && {
      qualities: asset.tags['Quality'].objects.map(({ value }) => value),
    }),
    ...(asset.metas['Year'] && {
      year: String(asset.metas['Year'].value),
    }),
  };
}

function processMovie(asset: KalturaTopShelfAsset): TopShelfMovieAsset {
  return {
    ...processVodAssetMetas(asset.metas),
    ...processCommonTopShelfAsset(asset),
    hasMediaFiles: Boolean(asset.mediaFiles?.length),
    providerName: getProviderNameFromMeta(asset.metas),
    type: 'movie',
    ...(asset.tags['BillingId'] && {
      billingPlans: asset.tags['BillingId'].objects.map(({ value }) => value),
    }),
    ...(asset.metas['DeeplinkURN'] && {
      deeplinkUrn: String(asset.metas['DeeplinkURN'].value),
    }),
    ...(asset.metas['Deeplink'] && {
      deeplink: String(asset.metas['Deeplink'].value),
    }),
    ...(asset.metas['duration'] && {
      duration: Number(asset.metas['duration'].value),
    }),
    ...(asset.metas['Is_Deeplink'] && {
      shouldUseDeeplink: Boolean(asset.metas['Is_Deeplink'].value),
    }),
  };
}

function processEpisode(asset: KalturaTopShelfAsset): TopShelfEpisodeAsset {
  return {
    ...processVodAssetMetas(asset.metas),
    ...processCommonTopShelfAsset(asset),
    hasMediaFiles: Boolean(asset.mediaFiles?.length),
    providerName: getProviderNameFromMeta(asset.metas),
    type: 'episode',
    ...(asset.tags['BillingId'] && {
      billingPlans: asset.tags['BillingId'].objects.map(({ value }) => value),
    }),
    ...(asset.metas['DeeplinkURN'] && {
      deeplinkUrn: String(asset.metas['DeeplinkURN'].value),
    }),
    ...(asset.metas['Deeplink'] && {
      deeplink: String(asset.metas['Deeplink'].value),
    }),
    ...(asset.metas['duration'] && {
      duration: Number(asset.metas['duration'].value),
    }),
    ...(asset.metas['EpisodeName'] && {
      episodeName: String(asset.metas['EpisodeName'].value),
    }),
    ...(asset.metas['EpisodeNumber'] && {
      episodeNumber: Number(asset.metas['EpisodeNumber'].value),
    }),
    ...(asset.metas['Is_Deeplink'] && {
      shouldUseDeeplink: Boolean(asset.metas['Is_Deeplink'].value),
    }),
    ...(asset.metas['SeasonNumber'] && {
      seasonNumber: Number(asset.metas['SeasonNumber'].value),
    }),
    ...(asset.metas['SeriesID'] && {
      seriesId: String(asset.metas['SeriesID'].value),
    }),
    ...(asset.tags['SeriesName'] && {
      seriesName: String(asset.tags['SeriesName'].objects[0].value),
    }),
  };
}

function processProgram(asset: KalturaTopShelfAsset): TopShelfProgramAsset {
  return {
    ...processCommonTopShelfAsset(asset),
    channelId: asset.linearAssetId,
    enableCatchUp: asset.enableCatchUp,
    endDate: asset.endDate * 1000,
    providerName: getProviderNameFromMeta(asset.metas),
    startDate: asset.startDate * 1000,
    type: 'program',
    ...(asset.metas['EpisodeName'] && {
      episodeName: String(asset.metas['EpisodeName'].value),
    }),
    ...(asset.metas['EpisodeNumber'] && {
      episodeNumber: Number(asset.metas['EpisodeNumber'].value),
    }),
    ...(asset.metas['SeasonNumber'] && {
      seasonNumber: Number(asset.metas['SeasonNumber'].value),
    }),
    ...(asset.metas['SeriesID'] && {
      seriesId: String(asset.metas['SeriesID'].value),
    }),
    ...(asset.tags['SeriesName'] && {
      seriesName: String(asset.tags['SeriesName'].objects[0].value),
    }),
  };
}

function processSeries(asset: KalturaTopShelfAsset): TopShelfSeriesAsset {
  return {
    ...processVodAssetMetas(asset.metas),
    ...processCommonTopShelfAsset(asset),
    hasMediaFiles: Boolean(asset.mediaFiles?.length),
    providerName: getProviderNameFromMeta(asset.metas),
    type: 'series',
    ...(asset.metas['Is_Deeplink'] && {
      shouldUseDeeplink: Boolean(asset.metas['Is_Deeplink'].value),
    }),
    ...(asset.metas['SeriesID'] && {
      seriesId: String(asset.metas['SeriesID'].value),
    }),
  };
}

function processPackage(asset: KalturaTopShelfAsset): TopShelfPackageAsset {
  return {
    description: asset.description,
    id: asset.id,
    images: processPhoenixImages(asset.images),
    isAdult: fromKalturaBoolean(asset.metas['Adult']),
    title: asset.name,
    type: 'package',
  };
}
